import { useEffect, useRef, useState } from 'react'
import {
  FilterBar,
  FilterClearButton,
  FilterPanelButton,
  FilterSearch,
  FilterSelect,
} from '@/components/ui/page-toolbar'
import { activeStages, closedStages, futureStages } from './deals-constants'
import { DealsFiltersPanel } from './DealsFiltersPanel'
import type { DealCloserOption } from '@/hooks/useDealsTeamClosers'
import type { MainTab } from '@/types'

interface DealsFiltersProps {
  searchTerm: string
  setSearchTerm: (v: string) => void
  closerFilter: string
  setCloserFilter: (v: string) => void
  showStagnantOnly: boolean
  setShowStagnantOnly: (v: boolean) => void
  etapaFilter: string
  setEtapaFilter: (v: string) => void
  periodFilter: string
  setPeriodFilter: (v: string) => void
  canFilterByCloser: boolean
  mainTab: MainTab
  closers?: DealCloserOption[]
}

function getStagesForTab(mainTab: MainTab) {
  switch (mainTab) {
    case 'active':
      return activeStages
    case 'closed':
      return closedStages
    case 'future':
      return futureStages
    default:
      return activeStages
  }
}

export function DealsFilters({
  searchTerm,
  setSearchTerm,
  closerFilter,
  setCloserFilter,
  showStagnantOnly,
  setShowStagnantOnly,
  etapaFilter,
  setEtapaFilter,
  periodFilter,
  setPeriodFilter,
  canFilterByCloser,
  mainTab,
  closers = [],
}: DealsFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersRootRef = useRef<HTMLDivElement>(null)

  const stagesForTab = getStagesForTab(mainTab)

  const advancedFilterCount = [
    showStagnantOnly,
    etapaFilter !== 'all',
    periodFilter !== 'all',
  ].filter(Boolean).length

  const hasActiveFilter =
    searchTerm.trim().length > 0 ||
    showStagnantOnly ||
    (canFilterByCloser && closerFilter !== 'all') ||
    etapaFilter !== 'all' ||
    periodFilter !== 'all'

  const clearAllFilters = () => {
    setSearchTerm('')
    setShowStagnantOnly(false)
    if (canFilterByCloser) setCloserFilter('all')
    setEtapaFilter('all')
    setPeriodFilter('all')
    setFiltersOpen(false)
  }

  useEffect(() => {
    if (!filtersOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersOpen(false)
    }

    const onPointerDown = (e: MouseEvent) => {
      if (filtersRootRef.current && !filtersRootRef.current.contains(e.target as Node)) {
        setFiltersOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [filtersOpen])

  return (
    <FilterBar>
      <FilterSearch
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder='Buscar deal, empresa ou serviço...'
        ariaLabel='Buscar deals'
      />

      {canFilterByCloser && (
        <FilterSelect
          value={closerFilter}
          onChange={setCloserFilter}
          title='Closer'
          ariaLabel='Filtrar por closer'
        >
          <option value='all'>Todos os Closers</option>
          {closers.map((closer) => (
            <option key={closer.id} value={closer.id}>
              {closer.name}
            </option>
          ))}
        </FilterSelect>
      )}

      <div ref={filtersRootRef} className='relative shrink-0'>
        <FilterPanelButton
          active={filtersOpen}
          badgeCount={advancedFilterCount}
          expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
        />
        <DealsFiltersPanel
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onClearAll={clearAllFilters}
          stagesForTab={stagesForTab}
          etapaFilter={etapaFilter}
          setEtapaFilter={setEtapaFilter}
          periodFilter={periodFilter}
          setPeriodFilter={setPeriodFilter}
          showStagnantOnly={showStagnantOnly}
          setShowStagnantOnly={setShowStagnantOnly}
          showCloserSelect={canFilterByCloser}
          closerFilter={closerFilter}
          setCloserFilter={setCloserFilter}
          closers={closers}
        />
      </div>

      {hasActiveFilter && <FilterClearButton onClick={clearAllFilters} />}
    </FilterBar>
  )
}
